import gql from 'graphql-tag';

import { Query, Mutation, QueryRegistry } from 'core/index';

import { PROJECT_CONNECTION_FIELDS } from 'apps/project/gql';
import { STORY_CONNECTION_FIELDS } from 'apps/story/gql';
import { COLLECTION_CONNECTION_FIELDS } from 'apps/collection/gql';
import { THREAD_CONNECTION_FIELDS } from 'apps/forum/gql';
import {
  PROFILE_FIELDS,
  INITIATIVE_FIELDS,
  INITIATIVE_CONNECTION_FIELDS,
} from 'apps/initiative/gql/fragments';

export const INITIATIVE_QUERY = gql`
  ${INITIATIVE_FIELDS}
  query Initiative($slug: String) {
    loggedInUser {
      id
      pendingInvites {
        id
        inviteDate
        status
        group {
          id
          space {
            id
            parent {
              id
              parent {
                id
              }
            }
          }
          name
        }
      }
    }
    initiative(slug: $slug) {
      ...initiativeFields

      inSpace {
        flags
      }
    }
  }
`;

export class InitiativeQuery extends Query {
  static conf = {
    GQL: INITIATIVE_QUERY,
    varNames: ['slug'],
  };
}

export const INITIATIVE_OVERVIEW_QUERY = gql`
  ${PROJECT_CONNECTION_FIELDS}
  ${STORY_CONNECTION_FIELDS}
  ${COLLECTION_CONNECTION_FIELDS}
  ${THREAD_CONNECTION_FIELDS}
  ${PROFILE_FIELDS}
  query Initiative(
    $slug: String
    $membersCount: Int
    $membersCursor: String
    $membersSortBy: String
    $projectsCount: Int
    $projectsCursor: String
    $projectsSortBy: String
    $storiesCount: Int
    $storiesCursor: String
    $storiesSortBy: String
    $threadBatchSize: Int
    $threadCursor: String
    $threadSortBy: String
  ) {
    initiative(slug: $slug) {
      id
      members(
        first: $membersCount
        after: $membersCursor
        sortBy: $membersSortBy
      ) {
        ...pConnectionFields
      }
      projects(
        first: $projectsCount
        after: $projectsCursor
        sortBy: $projectsSortBy
      ) {
        ...projectConnectionFields
      }
      stories(
        first: $storiesCount
        after: $storiesCursor
        sortBy: $storiesSortBy
      ) {
        ...storyConnectionFields
      }
      forum {
        id
        active
        threads(
          first: $threadBatchSize
          after: $threadCursor
          sortBy: $threadSortBy
        ) {
          ...threadConnectionFields
        }
      }
    }
  }
`;

export class InitiativeOverviewQuery extends Query {
  static conf = {
    GQL: INITIATIVE_OVERVIEW_QUERY,
    varNames: [
      'slug',
      'membersCount',
      'membersCursor',
      'membersSortBy',
      'projectsCount',
      'projectsCursor',
      'projectsSortBy',
      'storiesCount',
      'storiesCursor',
      'storiesSortBy',
      'collectionsCount',
      'collectionsCursor',
      'collectionsSortBy',
    ],
    resultPath: 'initiative',
  };
}

const INITIATIVES_QUERY = gql`
  ${INITIATIVE_CONNECTION_FIELDS}
  query Initiatives($batchSize: Int, $cursor: String, $sortBy: String) {
    initiatives(first: $batchSize, after: $cursor, sortBy: $sortBy) {
      ...initiativeConnectionFields
    }
  }
`;

export class InitiativesQuery extends Query {
  static conf = {
    GQL: INITIATIVES_QUERY,
    varNames: ['batchSize', 'cursor', 'sortBy'],
    resultPath: 'initiatives',
  };
}

export const INITIATIVE_MUTATION = gql`
  ${INITIATIVE_FIELDS}
  mutation Initiative($initiativeInput: InitiativeInput) {
    initiative(initiativeData: $initiativeInput) {
      initiative {
        ...initiativeFields
      }
    }
  }
`;

export class InitiativeMutation extends Mutation {
  static conf = {
    GQL: INITIATIVE_MUTATION,
    inputName: 'initiativeInput',
    resultPath: 'initiative',
  };
}

const INITIATIVE_MEMBERS = gql`
  query InitiativeMembers(
    $slug: String
    $membersCount: Int
    $membersCursor: String
    $membersSortBy: String
  ) {
    initiative(slug: $slug) {
      id
      members(
        first: $membersCount
        after: $membersCursor
        sortBy: $membersSortBy
      ) {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
        edges {
          cursor
          node {
            id
            fullName
            username
            imageFallbackChar
            avatar {
              id
              path
              url
              completed
            }
            content {
              id
              whitelabel
            }
          }
        }
      }
    }
  }
`;

export class InitiativeMembersQuery extends Query {
  static conf = {
    GQL: INITIATIVE_MEMBERS,
    varNames: ['slug', 'membersCount', 'membersCursor', 'membersSortBy'],
    resultPath: 'initiative.members',
  };
}

export const NEW_INITIATIVE_MUTATION = gql`
  mutation NewInitiative($initiativeInput: InitiativeInput) {
    initiative(initiativeData: $initiativeInput) {
      initiative {
        id
        title
        slug
      }
    }
  }
`;

export class NewInitiativeMutation extends Mutation {
  static conf = {
    GQL: NEW_INITIATIVE_MUTATION,
    inputName: 'initiativeInput',
    resultPath: 'initiative.initiative',
  };
}

export const INITIATIVE_STRIPE_CUSTOMER = gql`
  mutation InitiativeStripeCustomer($initiativeInput: InitiativeInput) {
    initiativeStripe(initiativeData: $initiativeInput) {
      initiative {
        id
      }
    }
  }
`;

export class InitiativeStripeCustomer extends Mutation {
  static conf = {
    GQL: INITIATIVE_STRIPE_CUSTOMER,
    inputName: 'initiativeInput',
    resultPath: 'initiative.initiative',
  };
}

export const AUTOCOMPLETE_INITIATIVES = gql`
  query Initiative($complete: String, $first: Int) {
    initiatives(first: $first, contains: ["slug", $complete]) {
      edges {
        cursor
        node {
          id
          slug
          avatar {
            id
            path
            url
          }
          imageFallbackChar
        }
      }
    }
  }
`;

export class AutocompleteInitiativesQuery extends Query {
  static conf = {
    GQL: AUTOCOMPLETE_INITIATIVES,
    varNames: ['complete', 'first'],
    resultPath: 'initiatives.edges',
  };
}

const INITIATIVE_CHECK = gql`
  query Initiative($slug: String) {
    initiative(slug: $slug) {
      id
      canUpdate
    }
  }
`;

export class InitiativeCheckQuery extends Query {
  static conf = {
    GQL: INITIATIVE_CHECK,
    varNames: ['slug'],
    resultPath: 'initiative',
  };
}

const ORGANIZATION_TYPES = gql`
  query OrganizationTypes {
    organizationTypes {
      id
      name
    }
  }
`;

const INITIATIVE_BILLING_SUBSCRIPTION = gql`
  query InitiativeSubscriptions($spaceSlug: String) {
    initiative(slug: $spaceSlug) {
      id
      promotion {
        id
        spaceId
        freeSeats
        discount
        earlyAccess
      }
    }
  }
`;

const EXTEND_FREE_TRIAL_PERIOD = gql`
  mutation ExtendFreeTrial($spaceId: ID!, $trialEnd: String!) {
    extendFreeTrial(spaceId: $spaceId, trialEnd: $trialEnd) {
      success
    }
  }
`;

export class ExtendFreeTrialMutation extends Mutation {
  static conf = {
    GQL: EXTEND_FREE_TRIAL_PERIOD,
    inputName: ['spaceId', 'trialEnd'],
  };
}

export class InitiativeBillingSubscriptionQuery extends Query {
  static conf = {
    GQL: INITIATIVE_BILLING_SUBSCRIPTION,
    varNames: ['spaceSlug'],
    resultPath: 'initiative',
  };
}

export class OrganizationTypesQuery extends Query {
  static conf = {
    GQL: ORGANIZATION_TYPES,
    resultPath: 'organizationTypes',
  };
}

QueryRegistry.add(
  'organization.OrganizationTypesQuery',
  OrganizationTypesQuery,
);
